import React from 'react';
import {observable, action, computed, makeObservable, runInAction, reaction} from 'mobx';
import {observer} from 'mobx-react';
import store from './s';
import Shipping from './components/shipping/shipping';
import CartSummary from './components/cart/cartSummary';
import Cart from './components/cart/cart';
import RecommendRow from './components/cart/ab-redesign-recommend';
import CheckoutAccount from './components/checkoutaccount/checkoutAccount';
import Payment from './components/payment/payment';
import BillingStep, {billingDefaultApplies} from './components/payment/ab-redesign-billing';
import '../style/style.scss';
import {checkout, CHECKOUT_REQUEST_FAILED_MODAL_ID} from '../api';
import Oci from './components/oci/oci';
import {ICheckoutStep} from './i';
import {cartItemNoun, getCartItemTotal} from './components/cart/cart-quantity/cart-quantity';
import Confidence from './components/confidence';
import PaypalAdv from './components/payment/paypal-advanced';
import ModalFastShipping from './components/modals/fast-shipping';
import ModalShippingInfoThirdParty from './components/modals/shipping-third-party-info';
import OrderProcessing from './components/modals/order-processing';
import SpecialProcessing from './components/modals/checkout-updating';
import ShippingEst from './components/modals/shipping-est-postal';
import ModalSimple from '../global/modal-simple';
import AbTestStore, {ABTestKeys} from '../global/store/ab-test-store';
import * as emitter from '../global/emitter';
import ModalCartMobile from './components/cart/ab-test/cart-mobile.modal';
import ReturnPolicyModal from './components/modals/return-policy.modal';
import {screenResizeStore} from '../global/store/screen-resize-store';
import {getPhoneHREF, Portal} from '../util';
import {sigExposure} from '../global/sig';
import {checkoutRedesignKey, isCheckoutRedesign, isMyFramesPage} from './ab-redesign';

declare var openz: any;
declare var __company_name: string;
declare var __domain_name: string;
declare var __is_production: string;

const defaultStyle = {
    transition: `opacity ${100}ms ease-in-out`,
    opacity: 0,
};

const transitionStyles = {
    entering: {opacity: 0},
    entered: {opacity: 1},
};

declare var OpenLHNChat: any;
declare const modalOpen: (id: string) => void;

@observer
class CheckoutComponent extends React.Component {
    @observable hasLoadedCart: boolean = false;
    @observable hasLoadedEstimates: boolean;
    @observable statePaymentIn: boolean = false;
    @observable hasError: boolean = false;
    @observable logo_png: string = `https://${Portal?.CDNDomain}/images/cached/general/logo_45.png`;
    @observable logo_webp: string = `https://${Portal?.CDNDomain}/images/cached/general/logo_45.webp`;
    disposeSiteHeaderCount?: () => void;

    constructor(props) {
        super(props);
        this.getCart = this.getCart.bind(this);
        this.getEstimated = this.getEstimated.bind(this);
        this.checkFastShipWarning = this.checkFastShipWarning.bind(this);
        this.ab_update = this.ab_update.bind(this);
        // this.hasLoadedCart = false;
        this.hasLoadedEstimates = false;
        this.hasError = false;
        makeObservable(this);
        window.react_checkout = this;

        if (Portal && Portal.PortalID > 0) {
            this.logo_png = `${Portal?.LogoURL}.png`;
            this.logo_webp = `${Portal?.LogoURL}.webp`;
        }
    }

    @action
    ab_update(_key: string, _value: number) {
        window.ab[_key] = _value;
        if (!AbTestStore.ab) {
            AbTestStore.ab = {};
        }
        AbTestStore.ab[_key] = _value;
        emitter.eventEmitter.dispatch(emitter.EventType.AB, 'ab');
    }

    @action
    resetAfterFatalError = () => {
        store.initCheckout();
        store.uiCheckout.requireReset = true;
        store.moveStep(ICheckoutStep.cart);
        this.hasError = false;
        this.hasLoadedCart = false;
        this.getCart();
    };

    componentDidMount() {
        if (store.checkout == undefined) {
            store.initCheckout();
        }
        // Both arms, at the shell that always renders (cart skip still mounts this component). MyFrames and framerly are not in the test.
        // store.checkout.isMyFrames is false until get-cart answers, so the page's synchronous __is_myframes is checked as well.
        if (store.checkout && !store.checkout.isMyFrames && !isMyFramesPage() && Portal?.PortalID !== 3 && __is_production === '1') {
            sigExposure(checkoutRedesignKey);
        }
        this.getCart();
        this.syncSiteHeader();
        // A qty change does not re-render this component (render only reads items.length), so the header count follows a reaction.
        this.disposeSiteHeaderCount = reaction(
            () => (this.showSiteHeader ? this.cartItemTotal : -1),
            count => {
                if (count >= 0) {
                    this.syncSiteHeaderCount(count);
                }
            },
            {fireImmediately: true},
        );
    }

    componentDidUpdate() {
        this.syncSiteHeader();
    }

    componentWillUnmount() {
        if (this.disposeSiteHeaderCount) {
            this.disposeSiteHeaderCount();
        }
        document.documentElement.classList.remove('ab-site-header');
    }

    /** The redesign arm shows the server-rendered site header on the cart step only; cart.cshtml hides it otherwise. */
    @computed get showSiteHeader(): boolean {
        return !!store.checkout && this.hasLoadedCart && isCheckoutRedesign(store.checkout.isMyFrames) && store.checkout.cstep <= ICheckoutStep.cart;
    }

    syncSiteHeader() {
        document.documentElement.classList.toggle('ab-site-header', this.showSiteHeader);
    }

    /**
     * The site header's cart link is rendered once by _header.cshtml (Cart_GetQty_Header), so on the B cart step it goes stale
     * after a qty change or a remove. Mirror the live count into it in the server's own format ("1 item", "2 items", "" for none).
     * Covers both header variants: the CMS header (a#cart-ada > span#cart-str) and the production header (#cart-new > a, and the
     * mobile menu li > a), each a /cart link with a trailing count span and a "Shopping Cart N items" aria-label. The icon-only
     * mobile link has no count span and is left alone. The header is not ours, so this never throws.
     */
    syncSiteHeaderCount(count: number) {
        try {
            const text = count < 1 ? '' : `${count.toLocaleString('en-US')} ${cartItemNoun(count)}`;
            const label = text ? `Shopping Cart ${text}` : 'Shopping Cart';
            document.querySelectorAll<HTMLAnchorElement>('#header-main a[href="/cart"]').forEach(link => {
                const spans = Array.from(link.children).filter(c => c.tagName === 'SPAN');
                const countSpan = spans.length > 1 ? spans[spans.length - 1] : null;
                if (!countSpan || countSpan.querySelector('i')) {
                    return;
                }
                countSpan.textContent = text;
                link.setAttribute('aria-label', label);
                const li = link.parentElement;
                if (li && li.tagName === 'LI' && li.hasAttribute('aria-label')) {
                    li.setAttribute('aria-label', label);
                }
            });
        } catch {
            // a stale header count is better than a broken checkout
        }
    }

    @action
    getCart = async () => {
        const hasLoadedCart = await checkout('get-cart');

        runInAction(() => {
            // approved ria
            this.hasLoadedCart = hasLoadedCart;
            if (store.checkout.ship === null) {
                store.checkout.ship = store.getEmptyShipping();
            }
            if (store.checkout.billing === null) {
                store.checkout.billing = store.getEmptyBilling();
            }
            // Redesign default "billing address is the same as shipping", client-side for now so the step list shows four
            // steps from the first paint; the Shipping address card syncs it to the server when it mounts.
            if (hasLoadedCart && isCheckoutRedesign(store.checkout.isMyFrames) && billingDefaultApplies()) {
                store.checkout.billing.addrSameAsShip = true;
            }

            this.getEstimated();
        });
    };

    @action
    checkFastShipWarning = () => {
        if (store.checkout.is_fast_shipping && store.checkout.is_fast_shipping_broken && !store.checkout.received_fast_ship_warning) {
            store.checkout.received_fast_ship_warning = true;
            modalOpen('modal-fast-shipping');
        }
    };

    @action
    getEstimated = async () => {
        if (store.checkout.cstep !== ICheckoutStep.cart || this.hasLoadedEstimates) {
            return;
        }
        this.hasLoadedEstimates = true;
        store.uiCheckout.isLoadingEstimated = true;
        await checkout('get-est');

        runInAction(() => {
            // approved ria
            store.uiCheckout.isLoadingEstimated = false;
            this.checkFastShipWarning();
        });
    };

    @computed get cartItemTotal() {
        return getCartItemTotal(store.checkout.cart.items);
    }

    // Control only (the redesign branch below does not read it): the control's promo-covered $0 total takes payment.tsx's
    // 'myf-place-order' button instead of PaypalAdv.
    @computed get isZeroOrder() {
        return (
            !store.checkout.isMyFrames && store.checkout.cstep === ICheckoutStep.payment && store.checkout.cart.pricing.total <= 0 && (store.checkout.cart.pricing.discount != 0 || store.checkout.cart.pricing.gift != 0) && store.checkout.code_promo.value && store.checkout.code_promo.value.length > 0
        );
    }

    @computed get isTestOrder() {
        try {
            return store.checkout.notes_customer.toLowerCase().includes('marketing-test-order') || store.checkout.notes_gift.toLowerCase().includes('marketing-test-order');
        } catch (err5) {
            return false;
        }
    }

    render() {
        if (store.checkout == undefined) {
            return <div>Error 0</div>;
        }

        const showCarSummary = AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) ? store.checkout.cstep !== ICheckoutStep.cart : true;
        const checkoutRedesign = isCheckoutRedesign(store.checkout.isMyFrames);
        // The redesign switches to the stacked (mobile) layout below Bootstrap lg (992px); the control keeps md (768px).
        const mobileLayout = screenResizeStore.width < (checkoutRedesign ? 992 : 768);
        // Cart step: the cart lines come first and the summary (totals + checkout buttons) follows them.
        const summaryAfterMain = checkoutRedesign && store.checkout.cstep <= ICheckoutStep.cart;
        // The cart list already shows the empty-cart message, so the stacked layout has no summary for an empty cart.
        const showMobileSummary = showCarSummary && mobileLayout && (!checkoutRedesign || (store.checkout.cart.items && store.checkout.cart.items.length > 0));

        return (
            <div className={`checkout font-${Portal?.PortalID}${checkoutRedesign ? ` ${checkoutRedesignKey}` : ''}`}>
                <div
                    className="checkout-v4-skin-header"
                    aria-label="Checkout navigation"
                >
                    <div className="logo">
                        <a
                            href={store.checkout.isMyFrames ? ' /MyFrames' : '/'}
                            aria-label={`${__company_name} | Diploma Frame | Go to homepage`}
                        >
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={this.logo_webp}
                                />
                                <img
                                    className="resp"
                                    src={this.logo_png}
                                    alt={`${__company_name} logo`}
                                    width={360}
                                    style={{aspectRatio: Portal?.PortalID === 0 ? '8/1' : '4.3/1', objectFit: 'contain'}}
                                    height={Portal?.PortalID === 0 ? '45' : '84'}
                                    loading="lazy"
                                />
                            </picture>
                        </a>
                    </div>

                    {!checkoutRedesign && store.checkoutABSkip && store.checkout.cstep === ICheckoutStep.oci && store.checkout.cart.items && store.checkout.cart.items.length > 0 && screenResizeStore.width > 767 && (
                        <div
                            className="continue-shopping"
                            style={{marginLeft: 25, marginRight: 'auto'}}
                        >
                            <a
                                href={store.getContinueShoppingLink()}
                                className="underline"
                                aria-label="Continue Shopping"
                                style={{fontSize: 14}}
                            >
                                Continue Shopping {store.checkout.cart.items && store.checkout.cart.items.length > 0 && store.checkout.cart.items[0].title1}
                            </a>
                        </div>
                    )}

                    {checkoutRedesign && (
                        <div className="ab-header-right">
                            <h1 className="ab-sr-only">Checkout</h1>
                            {Portal?.CustomerServicePhone && (
                                <a
                                    href={getPhoneHREF()}
                                    className="d-none d-md-inline"
                                >
                                    {Portal?.CustomerServicePhone}
                                </a>
                            )}
                            <a
                                href="/Identity/Account/Manage/Orders"
                                aria-label="Track order"
                            >
                                <i
                                    className="far fa-truck"
                                    aria-hidden={true}
                                />
                                <span>Track order</span>
                            </a>
                            <button
                                type="button"
                                className="text-btn"
                                aria-label="Chat"
                                aria-haspopup="dialog"
                                onClick={e => {
                                    openz();
                                    e.preventDefault();
                                }}
                            >
                                <i
                                    className="far fa-comments"
                                    aria-hidden={true}
                                />
                                <span>Chat</span>
                            </button>
                        </div>
                    )}

                    {!checkoutRedesign && (
                        <div>
                            <div>
                                {store.checkout.isMyFrames && (
                                    <a
                                        href="/MyFrames"
                                        aria-label="Back to MyFrames"
                                    >
                                        <h1 className="zero">MyFrames Checkout</h1>
                                    </a>
                                )}
                                {!store.checkout.isMyFrames && <h1 className="zero">Checkout</h1>}
                            </div>
                            <div>
                                <span className="chat-desktop d-none d-md-inline">
                                    {Portal?.CustomerServicePhone && (
                                        <a
                                            href={getPhoneHREF()}
                                            style={{color: '#595959'}}
                                        >
                                            {Portal?.CustomerServicePhone}
                                        </a>
                                    )}
                                    <span style={{paddingLeft: 12, paddingRight: 4, whiteSpace: 'nowrap'}}>
                                        <span style={{paddingRight: 4, color: '#595959'}}>
                                            <i
                                                className="fas fa-comment-alt-dots"
                                                aria-hidden={true}
                                            />
                                        </span>
                                        <button
                                            style={{color: '#595959'}}
                                            onClick={e => {
                                                openz();
                                                e.preventDefault();
                                            }}
                                            className="text-btn"
                                            aria-label="Chat"
                                            type="button"
                                            aria-haspopup="dialog"
                                        >
                                            Chat
                                        </button>
                                    </span>
                                </span>
                                <span
                                    style={{whiteSpace: 'nowrap'}}
                                    className="d-none d-md-inline"
                                >
                                    <button
                                        className="link cart-icon text-btn"
                                        onClick={e => {
                                            store.moveStep(ICheckoutStep.cart);
                                            e.preventDefault();
                                        }}
                                        aria-label="Back to cart"
                                        type="button"
                                    >
                                        <span style={{paddingRight: 6}}>
                                            <i
                                                className="fas fa-shopping-cart"
                                                aria-hidden={true}
                                            />
                                        </span>
                                        {this.cartItemTotal}
                                    </button>
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {checkoutRedesign && !this.hasError && this.hasLoadedCart && store.checkout.cstep >= ICheckoutStep.oci && <h2 className="ab-checkout-title">Checkout</h2>}
                {checkoutRedesign && !this.hasError && this.hasLoadedCart && store.checkout.cstep === ICheckoutStep.checkout && <h2 className="ab-checkout-title">Choose how you would like to check out</h2>}

                {!this.hasLoadedCart && (
                    <div
                        className="checkout-loader"
                        role="status"
                        aria-live="polite"
                    >
                        <div>
                            <i
                                className="fas fa-circle-notch fa-spin fast-spin"
                                aria-hidden={true}
                            ></i>
                        </div>
                        <div>Retrieving Cart</div>
                    </div>
                )}

                {!this.hasError && this.hasLoadedCart && (
                    <div className="row no-gutters">
                        {store.checkout.cstep === ICheckoutStep.cart && AbTestStore.ABIsVariant(ABTestKeys.ab_checkout_1, 1) && (
                            <div className="d-block d-md-none">
                                <ModalCartMobile />
                            </div>
                        )}

                        {showMobileSummary && !summaryAfterMain && (
                            <div className="col-12">
                                <CartSummary isMobile={true} />
                            </div>
                        )}

                        <div className={checkoutRedesign ? 'col-12 col-lg-8 ab-col-main' : 'col-12 col-md-8 col-md-8 order-2 order-sm-1'}>
                            {store.checkout.cstep >= ICheckoutStep.oci && <Oci />}
                            {store.checkout.cstep === ICheckoutStep.cart && <Cart />}
                            {store.checkout.cstep === ICheckoutStep.checkout && <CheckoutAccount />}
                            {store.checkout.cstep >= ICheckoutStep.oci && <Shipping />}
                            {checkoutRedesign && store.checkout.cstep >= ICheckoutStep.oci && <BillingStep />}
                            {checkoutRedesign && store.checkout.cstep >= ICheckoutStep.oci && (
                                <Payment>
                                    {/* One React-owned div is the conditional node, as in the control below: the PayPal SDK and apple_init move or remove nodes inside PaypalAdv, and React must never removeChild those itself.
                                        Not gated on isZeroOrder: a covered total (gift card or promo code) stays on PaypalAdv, whose "Place Order" takes the server's skipPayment path; the redesign never offers the MyFrames 'myf-place-order' button. */}
                                    {!this.isTestOrder && store.checkout.cstep === ICheckoutStep.payment && (
                                        <div className="ab-paypal-wrap">
                                            <PaypalAdv />
                                        </div>
                                    )}
                                </Payment>
                            )}
                            {!checkoutRedesign && store.checkout.cstep >= ICheckoutStep.oci && <Payment />}
                            {!checkoutRedesign && !store.checkout.isMyFrames && !this.isZeroOrder && !this.isTestOrder && store.checkout.cstep === ICheckoutStep.payment && (
                                <div
                                    className={store.checkout.cstep === ICheckoutStep.payment ? '' : 'vv-none'}
                                    style={{maxWidth: 750, margin: '0 auto', paddingTop: 12}}
                                >
                                    <PaypalAdv />
                                </div>
                            )}

                            <div id="yotpo-portal-desktop-inner" />
                        </div>
                        {/* Redesign, stacked layout: the design repeats the full Order summary + Shopping cart below the step cards (the top instance is the collapsed Subtotal / TOTAL header). */}
                        {checkoutRedesign && mobileLayout && showCarSummary && store.checkout.cstep >= ICheckoutStep.oci && store.checkout.cart.items && store.checkout.cart.items.length > 0 && (
                            <div className="col-12">
                                <CartSummary
                                    isMobile={true}
                                    stackedDetail={true}
                                />
                            </div>
                        )}
                        {showMobileSummary && summaryAfterMain && (
                            <div className="col-12">
                                <CartSummary isMobile={true} />
                            </div>
                        )}
                        {!mobileLayout && (
                            <div className={checkoutRedesign ? 'col-12 col-lg-4 ab-col-summary' : 'col-12 col-md-4 col-lg-4 order-1 order-sm-2'}>
                                <CartSummary isMobile={false} />
                            </div>
                        )}
                        {checkoutRedesign && store.checkout.cstep === ICheckoutStep.cart && Portal && Portal.PortalID < 1 && (
                            <div className="col-12">
                                <RecommendRow />
                            </div>
                        )}
                    </div>
                )}

                {this.hasError && (
                    <div
                        style={{paddingTop: 24}}
                        role="alert"
                        aria-live="assertive"
                        aria-atomic="true"
                        id="checkout-error-alert"
                    >
                        <hr />A checkout error has occurred.
                        <ul>
                            <li>If you placed an order, we likely received payment for your order. Please contact us at {Portal?.CustomerServicePhone} to verify your order.</li>
                            <li>Otherwise please click here to reset your cart: </li>
                        </ul>
                        <div style={{paddingLeft: '5%'}}>
                            <button
                                type="button"
                                className={`btn btn-checkout btn-checkout-yellow${Portal?.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}`}
                                aria-label="Reset cart and continue"
                                aria-describedby="checkout-error-alert checkout-error-details"
                                onClick={this.resetAfterFatalError}
                            >
                                Continue
                            </button>
                        </div>
                        <div style={{paddingTop: 48}}>Error details:</div>
                        <div
                            className="alert alert-danger"
                            id="checkout-error-details"
                        >
                            <div className="b">{store.errorLog}</div>
                            <div>{JSON.stringify(store.errorLogInfo)}</div>
                        </div>
                    </div>
                )}

                <div id="yotpo-portal-desktop" />

                <div
                    className={checkoutRedesign ? 'd-none d-lg-block' : 'd-none d-md-block'}
                    style={{marginTop: '7.5%'}}
                />

                {!checkoutRedesign && (
                    <div className="row d-block d-md-none">
                        <div
                            className="col-12"
                            style={{padding: '20px 0'}}
                        >
                            <div className="hr" />
                            <Confidence />
                            <div className="hr" />
                        </div>
                    </div>
                )}

                <div id="yotpo-portal-mobile" />

                {!checkoutRedesign && (
                    <div className="row row-footer">
                        <div className="col-12 col-md-9 col-footer col-footer-1">
                            {Portal?.CustomerServicePhone && (
                                <span>
                                    <a href={getPhoneHREF()}>{Portal?.CustomerServicePhone}</a>
                                </span>
                            )}
                            {Portal && Portal.PortalID < 1 && (
                                <span>
                                    <button
                                        style={{fontSize: '12px'}}
                                        onClick={e => {
                                            openz();
                                            e.preventDefault();
                                        }}
                                        className="text-btn"
                                        type="button"
                                        aria-label="Chat"
                                        aria-haspopup="dialog"
                                    >
                                        <span style={{paddingRight: 4, color: '#999'}}>
                                            <i
                                                className="fas fa-comment-alt-dots"
                                                aria-hidden={true}
                                            />
                                        </span>{' '}
                                        Chat
                                    </button>
                                </span>
                            )}
                            {Portal && Portal.PortalID < 4 && (
                                <span
                                    tabIndex={0}
                                    className="link pointer underline"
                                    onClick={e => {
                                        modalOpen('modal-return-policy');
                                        e.preventDefault();
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            modalOpen('modal-return-policy');
                                            e.preventDefault();
                                        }
                                    }}
                                >
                                    {Portal?.PortalID === 3 ? '30' : '120'}-Day Return Policy
                                </span>
                            )}
                            {Portal && Portal.PortalID < 1 && (
                                <span>
                                    <a
                                        href="/customer-service/frequently-asked-questions-faq"
                                        tabIndex={0}
                                    >
                                        FAQ
                                    </a>
                                </span>
                            )}
                            {Portal && Portal.PortalID < 1 && (
                                <span>
                                    <a
                                        href="/customer-service"
                                        tabIndex={0}
                                    >
                                        Customer Service
                                    </a>
                                </span>
                            )}
                            <span>
                                <a
                                    href={Portal && Portal.PortalID < 3 ? '/about-us/accessibility-statement' : '/accessibility-statement'}
                                    tabIndex={0}
                                >
                                    Accessibility Statement
                                </a>
                            </span>

                            <span style={{fontSize: '12px', cursor: 'default'}}>
                                Secure Checkout{' '}
                                <span style={{paddingLeft: 4}}>
                                    <i
                                        className="fas fa-lock"
                                        aria-hidden={true}
                                    ></i>
                                </span>
                            </span>
                        </div>

                        <div
                            className="col-12 col-md-3 col-footer text-right"
                            style={{fontSize: '12px'}}
                        >
                            <span style={{cursor: 'inherit'}}>
                                &copy; {__company_name} {new Date().getFullYear().toString()}
                            </span>
                        </div>
                    </div>
                )}

                <ModalShippingInfoThirdParty />
                <ReturnPolicyModal />
                <ModalFastShipping />
                <ShippingEst />
                {store.uiCheckout.processing && <OrderProcessing />}
                {store.uiCheckout.isSpecialProcessing && <SpecialProcessing />}

                <ModalSimple
                    id="modal-missing-shipping"
                    title={'Shipping Information Missing'}
                >
                    <div className="modal-body text-left">Your shipping information could not be obtained from your payment provider. Please input your address and continue to checkout - your preferred payment method can still be used.</div>
                </ModalSimple>

                <ModalSimple
                    id={CHECKOUT_REQUEST_FAILED_MODAL_ID}
                    title={'Please Refresh the Page'}
                    closeButtonText="Refresh Page"
                    saveAndClose={() => window.location.reload()}
                >
                    <div className="modal-body text-left">We couldn't update your checkout. Please refresh the page and try again - your cart will still be there.</div>
                </ModalSimple>
            </div>
        );
    }
}
export default CheckoutComponent;
